import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import SEO from '../components/SEO';

export default function PrivacyPolicy() {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const sectionStyle = {
    marginBottom: '40px',
  };

  const headingStyle = {
    fontSize: '20px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    marginBottom: '12px',
  };

  const bodyStyle = {
    fontSize: '15px',
    color: theme.colors.textSecondary,
    lineHeight: '1.8',
    margin: '0 0 12px 0',
  };

  const listStyle = {
    fontSize: '15px',
    color: theme.colors.textSecondary,
    lineHeight: '1.8',
    paddingLeft: '20px',
    margin: '0 0 12px 0',
  };

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgPrimary, color: theme.colors.textPrimary }}>
      <SEO
        title="Privacy Policy | Trubonn"
        description="How Trubonn collects, stores, and protects your personal information, in line with the Australian Privacy Act 1988."
        path="/privacy"
      />

      {/* Nav */}
      <nav style={{
        padding: '20px 40px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.colors.border}`,
        position: 'sticky',
        top: 0,
        background: theme.colors.bgPrimary,
        zIndex: 1000,
      }}>
        <div onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
          <img src="/logo.svg" alt="Trubonn Logo" style={{ height: '70px', width: '180px' }} />
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/')}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '15px', padding: '8px 16px' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
          >
            Home
          </button>
          <button
            onClick={() => navigate('/pricing')}
            style={{ background: 'transparent', border: 'none', color: theme.colors.textSecondary, cursor: 'pointer', fontSize: '15px', padding: '8px 16px' }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.colors.brand}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.colors.textSecondary}
          >
            Pricing
          </button>
        </div>
      </nav>

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '80px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: '56px' }}>
          <h1 style={{
            fontSize: '48px',
            fontWeight: 800,
            marginBottom: '16px',
            background: theme.gradients.brand,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            Privacy Policy
          </h1>
          <p style={{ color: theme.colors.textSecondary, fontSize: '15px', margin: 0 }}>
            Last updated: March 2026
          </p>
          <p style={{ color: theme.colors.textSecondary, fontSize: '15px', marginTop: '12px' }}>
            This Privacy Policy explains how Trubonn ("we", "us", or "our") collects, uses, stores, and protects your personal information when you use our cloud file storage service at trubonn.com.au. By using Trubonn, you agree to the practices described in this policy.
          </p>
        </div>

        {/* 1. Who We Are */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>1. Who We Are</h2>
          <p style={bodyStyle}>
            Trubonn is a cloud-based file storage service operated in Australia. We are committed to protecting your privacy and handling your personal information in accordance with the Australian Privacy Act 1988 and the Australian Privacy Principles (APPs).
          </p>
          <p style={bodyStyle}>
            For any privacy-related enquiries, you can contact us at{' '}
            <a href="mailto:admin@trubonn.com.au" style={{ color: theme.colors.brand }}>
              admin@trubonn.com.au
            </a>.
          </p>
        </div>

        {/* 2. Information We Collect */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Information We Collect</h2>
          <p style={bodyStyle}>We collect the following types of information:</p>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Account Information</p>
          <ul style={listStyle}>
            <li>Name and email address provided during registration</li>
            <li>A unique user identifier assigned to your account</li>
          </ul>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Payment Information</p>
          <ul style={listStyle}>
            <li>Billing details processed securely by Stripe. We do not store your credit card number or full payment details on our systems.</li>
            <li>Subscription status and transaction history</li>
          </ul>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Files and Content</p>
          <ul style={listStyle}>
            <li>Files you upload to Trubonn, stored securely on Microsoft Azure Blob Storage</li>
            <li>File metadata such as filename, size, type, and upload date</li>
          </ul>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Usage Information</p>
          <ul style={listStyle}>
            <li>Storage usage metrics</li>
            <li>Upload and access activity logs for security and rate limiting purposes</li>
            <li>IP address (used for geo-restriction to Australian users during registration)</li>
          </ul>
          <p style={{ ...bodyStyle, fontWeight: 600, color: theme.colors.textPrimary }}>Communications</p>
          <ul style={listStyle}>
            <li>Messages you send us via the contact form</li>
            <li>Email address for service notifications such as trial reminders and payment receipts</li>
          </ul>
        </div>

        {/* 3. How We Use Your Information */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. How We Use Your Information</h2>
          <p style={bodyStyle}>We use your information to:</p>
          <ul style={listStyle}>
            <li>Provide, operate, and maintain the Trubonn service</li>
            <li>Process payments and manage your subscription via Stripe</li>
            <li>Send transactional emails such as welcome messages, trial reminders, payment confirmations, and account notifications</li>
            <li>Respond to your support and contact form enquiries</li>
            <li>Enforce usage limits such as storage quotas and upload rate limits</li>
            <li>Verify that new registrations originate from Australia</li>
            <li>Improve the security and performance of our service</li>
          </ul>
          <p style={bodyStyle}>
            We do not use your data for advertising purposes, and we do not sell your personal information to third parties.
          </p>
        </div>

        {/* 4. Data Storage and Security */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Data Storage and Security</h2>
          <p style={bodyStyle}>
            Your files are stored on secure cloud infrastructure hosted in Australia. User authentication is managed by a trusted third-party identity provider. Payment processing is handled by Stripe, which is PCI-DSS compliant.
          </p>
          <p style={bodyStyle}>
            We implement industry-standard security measures including private storage, token-based access controls, and HTTPS encryption for all data in transit. Access to your files requires authentication and is restricted to your account only.
          </p>
          <p style={bodyStyle}>
            While we take reasonable steps to protect your information, no method of transmission or storage is 100% secure. You are responsible for keeping your account credentials confidential.
          </p>
        </div>

        {/* 5. Data Retention */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>5. Data Retention</h2>
          <p style={bodyStyle}>
            We retain your data for as long as your account is active. When you delete your account, all of your files, account data, and personal information are permanently and immediately deleted from our systems. This action cannot be undone.
          </p>
          <p style={bodyStyle}>
            Payment records may be retained by Stripe in accordance with their own privacy policy and applicable financial regulations.
          </p>
        </div>

        {/* 6. Third-Party Services */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Third-Party Services</h2>
          <p style={bodyStyle}>We use the following third-party services to operate Trubonn:</p>
          <ul style={listStyle}>
            <li><strong style={{ color: theme.colors.textPrimary }}>Cloud Infrastructure Provider</strong> — cloud infrastructure, file storage, and user authentication</li>
            <li><strong style={{ color: theme.colors.textPrimary }}>Stripe</strong> — payment processing and subscription management</li>
            <li><strong style={{ color: theme.colors.textPrimary }}>Email Service Provider</strong> — transactional email delivery</li>
            <li><strong style={{ color: theme.colors.textPrimary }}>ip-api.com</strong> — IP geolocation used to verify Australian residency at registration</li>
          </ul>
          <p style={bodyStyle}>
            Each of these providers has their own privacy policies governing how they handle data. We encourage you to review their policies if you have concerns.
          </p>
        </div>

        {/* 7. Cookies and Local Storage */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Cookies and Local Storage</h2>
          <p style={bodyStyle}>
            Trubonn uses browser local storage to store authentication tokens, user preferences such as theme settings, and recently uploaded file information. This data is stored locally on your device and is not transmitted to our servers.
          </p>
          <p style={bodyStyle}>
            We do not use advertising cookies or third-party tracking cookies.
          </p>
        </div>

        {/* 8. Your Rights */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. Your Rights</h2>
          <p style={bodyStyle}>Under the Australian Privacy Act, you have the right to:</p>
          <ul style={listStyle}>
            <li>Access the personal information we hold about you</li>
            <li>Request correction of inaccurate information</li>
            <li>Request deletion of your account and associated data</li>
            <li>Lodge a complaint with the Office of the Australian Information Commissioner (OAIC) if you believe we have mishandled your information</li>
          </ul>
          <p style={bodyStyle}>
            To exercise any of these rights, please contact us at{' '}
            <a href="mailto:admin@trubonn.com.au" style={{ color: theme.colors.brand }}>
              admin@trubonn.com.au
            </a>.
          </p>
        </div>

        {/* 9. Children's Privacy */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Children's Privacy</h2>
          <p style={bodyStyle}>
            Trubonn is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will delete it promptly.
          </p>
        </div>

        {/* 10. Changes to This Policy */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Changes to This Policy</h2>
          <p style={bodyStyle}>
            We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date at the top of this page. We encourage you to review this policy periodically. Continued use of Trubonn after any changes constitutes your acceptance of the updated policy.
          </p>
        </div>

        {/* 11. Contact Us */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Contact Us</h2>
          <p style={bodyStyle}>
            If you have any questions or concerns about this Privacy Policy or how we handle your data, please contact us:
          </p>
          <div style={{
            background: theme.colors.bgSecondary,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: '12px',
            padding: '24px',
          }}>
            <p style={{ color: theme.colors.textPrimary, margin: '0 0 8px', fontWeight: 600 }}>Trubonn</p>
            <p style={{ color: theme.colors.textSecondary, margin: '0 0 4px', fontSize: '14px' }}>
              Email:{' '}
              <a href="mailto:admin@trubonn.com.au" style={{ color: theme.colors.brand }}>
                admin@trubonn.com.au
              </a>
            </p>
            <p style={{ color: theme.colors.textSecondary, margin: 0, fontSize: '14px' }}>
              Website: <a href="https://trubonn.com.au" style={{ color: theme.colors.brand }}>trubonn.com.au</a>
            </p>
          </div>
        </div>

      </div>

      {/* Footer */}
      <footer style={{
        padding: '40px',
        textAlign: 'center',
        borderTop: `1px solid ${theme.colors.border}`,
      }}>
        <p style={{ color: theme.colors.textSecondary, fontSize: '14px', margin: 0 }}>
          © 2026 Trubonn Systems. All rights reserved.
        </p>
        <div style={{ marginTop: '16px', display: 'flex', gap: '24px', justifyContent: 'center' }}>
          <a href="/contact" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>Contact</a>
          <a href="/pricing" style={{ color: theme.colors.textSecondary, textDecoration: 'none', fontSize: '14px' }}>Pricing</a>
        </div>
      </footer>
    </div>
  );
}
